'use client'

import React, { FC, ReactElement } from 'react'
import useTokenOutpoints from '@/components/search-input/hooks/useTokenOutpoints'
import { Slider } from '@/components/ui/slider'
import formatNumber from '@/utils/format-number'
import useExchangeRate from '@/utils/hooks/useExchangeRate'
import { formatUsdPer } from '@/utils/format-usd'
import { EXPLORER_URL, API_URL, NETWORK } from '@/utils/constants'
import Wallet from '@/utils/wallet'
import axios from 'axios'
import { useToast } from '@/components/ui/use-toast'
import { ToastAction } from '@/components/ui/toast'
import isAlkanes from '@/utils/is-alkanes'
import { formatSatoshis } from '@/utils/hooks/useSatoshiPrice'
import isRune from '@/utils/is-rune'
import { CircleHelp } from 'lucide-react'

import useSelect from '../../hooks/useSelect'

import Tabs from './tabs'

import Invoice from '../../components/invoice'
import { Loading } from '@/components/ui/loading'
import SelectButton from '../../components/select-button'
import { listingNoun } from './listing-noun'
import Item from './base-item'

const CollectionDetailSplit: FC<{
	tab: string
	onChangeTab: (v: string) => void
	onNavigate: (v: string) => void
	onClose: () => void
	args: any
	floorSats: string | number
	token: any
	classification: string
}> = (props): ReactElement => {
	const { floorSats, onClose, args, tab, onChangeTab, token, classification } = props
	const { slug } = args
	const { toast } = useToast()

	//
	// Local State
	//

	const [split, setSplit] = React.useState(1)

	//
	// Remote Data
	//

	const { exchangeRate } = useExchangeRate()
	const { data, isLoading } = useTokenOutpoints(token)

	const ids = React.useMemo(() => {
		if (!data || !Array.isArray(data)) {
			return []
		}

		return data.map((e) => e.outpoint)
	}, [data])

	const { selected, setSelected, onSelect, onSelectAll, allSelected } = useSelect(ids)

	// Auto-select the biggest lot when entering split tab
	React.useEffect(() => {
		if (data && data.length > 0 && selected.size === 0) {
			const biggest = [...data].sort((a, b) => parseFloat(b.amount) - parseFloat(a.amount))[0]
			if (biggest) {
				setSelected(new Set([biggest.outpoint]))
			}
		}
	}, [data, selected.size, setSelected])

	//
	// Memos
	//

	const { selectedValue } = React.useMemo(() => {
		let selectedValue = 0

		if (!data) return { selectedValue }

		const byOutpoint = new Map((data || []).map((a: any) => [a.outpoint, a]))
		// @ts-ignore
		for (let e of selected.entries()) {
			const id = e[0]
			const match = byOutpoint.get(id)
			const amount = match ? parseFloat(match.amount) : 0
			selectedValue += amount
		}

		return {
			selectedValue
		}
	}, [selected, data])

	//
	// Renders
	//

	const handleChangeSplit = React.useCallback((v: any) => setSplit(v[0]), [])

	const renderOutpoint = React.useCallback(
		(e: any) => {
			const id = e.outpoint
			return <Item data={e} selected={selected.has(id)} onSelect={() => onSelect(id)} slug={slug} />
		},
		[onSelect, selected, slug]
	)

	const handleSubmit = React.useCallback(
		async (psbt: any) => {
			const rawtx = await Wallet.signPSBTExtractTx(psbt)
			const { data } = await axios.post(`${API_URL}/wallet/broadcast`, { rawtx: rawtx })

			if (data.error) {
				// Scriptpubkey
				if (data.error.code === -26) {
					throw new Error('Too many outputs for this amount. Choose fewer.')
				} else {
					toast({
						title: 'Failed to broadcast',
						description: data.error.message
					})
				}
			} else {
				toast({
					title: 'Success!',
					description: `You split ${formatNumber(selectedValue)} ${listingNoun(classification)}`,
					action: (
						<ToastAction
							altText="View Transaction"
							onClick={() => window.open(`${EXPLORER_URL}/tx/${data.result}`, '_blank')}
						>
							View Transaction
						</ToastAction>
					)
				})
				onClose()
			}
		},
		[toast, selectedValue, classification, onClose]
	)

	const handleBuildPsbt = React.useCallback(
		async (deps: any[], feeRate: number) => {
			const [dSelected, dSplit] = deps

			if (dSelected.size) {
				const outpoints = []
				const divisibility = isAlkanes(slug) ? 8 : token.divisibility
				const edicts = new Array(dSplit)
					.fill('')
					.map((_, i) => [
						token.rune_id,
						i === dSplit - 1 ? 0 : Math.floor(selectedValue / dSplit),
						divisibility,
						Wallet.getTaprootAddress()
					])

				const dataByOutpoint = new Map((data || []).map((d: any) => [d.outpoint, d]))
				// @ts-ignore
				for (let e of dSelected.entries()) {
					const id = e[0]
					const outpoint = dataByOutpoint.get(id)
					if (!outpoint) continue
					outpoints.push([id, outpoint.sats])
				}

				const { psbt, fee } = isAlkanes(slug)
					? await Wallet.getAlkaneTransfer(edicts, outpoints, feeRate)
					: await Wallet.getRuneTransfer(edicts, outpoints, feeRate)

				return { psbt, fee }
			}
		},
		[data, token, selectedValue, slug]
	)

	// @ts-ignore
	const usdPer = (((selectedValue / split) * floorSats) / 1e8) * exchangeRate || 0
	const quantityPerLotValue = `${formatNumber(
		formatUsdPer(selectedValue / split).usd
	)} / $${formatNumber(formatUsdPer(usdPer).usd)}`

	// Create invoice items array
	const invoiceItems = React.useMemo(() => {
		const items = []
		const formatFeeValue = (amount: number) =>
			`${formatSatoshis(amount)} ${NETWORK} / $${formatNumber(
				((amount / 1e8) * exchangeRate)?.toFixed(2)
			)}`

		if (isRune(slug) || isAlkanes(slug)) {
			items.push({
				title: 'Service Fee',
				valueFunc: ({ building, psbt }: any) => {
					if (building || !psbt) return '--'
					const serviceFee = Wallet.getServiceFee(psbt)
					return formatFeeValue(serviceFee)
				}
			})
		}

		// Add Network Fee last
		items.push({ title: 'Network Fee' })

		if (isRune(slug) || isAlkanes(slug)) {
			items.push({
				title: 'Total Fee',
				emphasize: true,
				valueFunc: ({ building, fee, psbt }: any) => {
					if (building || !psbt) return '--'
					const serviceFee = Wallet.getServiceFee(psbt)
					const totalFee = Number(fee) + serviceFee
					return formatFeeValue(totalFee)
				}
			})
		}

		return items
	}, [slug, exchangeRate])

	return (
		<>
			<div className="flex px-2 h-7 w-full justify-between items-center">
				<p className="text-muted-foreground text-xs font-medium">My Wallet</p>
			</div>
			<div className="flex px-2 py-1.5 gap-4">
				<Tabs value={tab} onValueChange={onChangeTab} slug={slug} />
			</div>
			<div>
				<div className="flex items-center gap-2 p-2">
					<div className="flex items-center gap-1 shrink-0">
						<p className="text-xs whitespace-nowrap">Number of Outputs:</p>
						<div className="relative group">
							<CircleHelp className="size-3 text-muted-foreground cursor-help" />
							<div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 hidden group-hover:block z-50 w-[200px] rounded-md border bg-popover px-3 py-1.5 text-xs text-popover-foreground shadow-md">
								Use this slider to decide the number of outputs to create from the selected token
								clips.
							</div>
						</div>
					</div>
					<div className="flex-1">
						<Slider
							defaultValue={[split]}
							value={[split]}
							max={5}
							min={1}
							step={1}
							onValueChange={handleChangeSplit}
							className="[&_[role=slider]]:size-3.5"
						/>
					</div>
					<input
						type="number"
						min={1}
						max={5}
						value={split}
						onChange={(e) => {
							const val = parseInt(e.target.value, 10)
							if (!isNaN(val)) setSplit(Math.max(1, Math.min(5, val)))
						}}
						className="w-12 h-7 text-center text-xs font-medium text-foreground bg-background border border-border rounded focus:outline-none focus:border-border transition-colors [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
					/>
				</div>
				<div className="mx-2 flex items-center justify-between pb-2">
					<p className="text-xs font-semibold text-foreground">Quantity Per Lot</p>
					<p className="text-xs font-semibold text-muted-foreground">{quantityPerLotValue}</p>
				</div>
			</div>
			<div className="flex justify-between items-center p-2">
				<div className="flex gap-2 items-center">
					<SelectButton selected={allSelected} onSelect={onSelectAll} />
					<p className="text-xs text-muted-foreground select-none">
						Quantity ({selected.size > 0 ? `${selected.size}/` : ''}
						{data?.length} lots)
					</p>
				</div>
			</div>
			{isLoading && (
				<div className="flex items-center justify-center min-h-[120px]">
					<Loading size={16} />
				</div>
			)}
			{data?.map(renderOutpoint)}
			<Invoice
				open={selected.size > 0}
				onSubmit={handleSubmit}
				onBuild={handleBuildPsbt as any}
				buildDeps={[selected, split, !!data]}
				action={`Split ${formatNumber(selectedValue)} ${classification}`}
				items={invoiceItems}
			/>
		</>
	)
}

export default CollectionDetailSplit
